import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { useAppSelector, useAppDispatch } from '../../../store/hooks'
import { taskUrl } from '@/utils/taskPath'
import { openConfirmDialog, setOpenDropdown, setOpenModal } from '../../../store/ui/uiSlice'
import { selectOpenDropdownId, selectOpenModal } from '../../../store/ui/uiSelectors'
import { selectAuth } from '../../../store/auth/authSelectors'
import { markWatchedSaga, type Notification } from '../../../store/notifications/notificationsSlice'
import { selectAllNotifications } from '../../../store/notifications/notificationsSelectors'
import { subscribeTaskActivitySaga, unsubscribeTaskActivitySaga } from '../../../store/activity/activitySlice'
import { useTask } from '../../../contexts/TaskContext'
import { useWorkspace } from '../../../contexts/WorkspaceContext'
import { uploadAttachment } from '../../../lib/uploadAttachment'
import IconClose from '../../../assets/icons/icon-close.svg?react'
import IconOptions from '../../../assets/icons/icon-options.svg?react'
import IconArrowRight from '../../../assets/icons/icon-arrow-right.svg?react'
import IconArrowLeft from '../../../assets/icons/icon-arrow-left.svg?react'
import IconCheck from '../../../assets/icons/icon-check.svg?react'
import IconButton from '@/components/ui/IconButton/IconButton'
import ButtonRound from '@/components/ui/ButtonRound/ButtonRound'
import DropdownWrapper from '@/components/ui/Dropdown/components/DropdownWrapper/DropdownWrapper'
import Dropdown from '../../ui/Dropdown/Dropdown'
import DropdownItem from '@/components/ui/Dropdown/components/DropdownItem/DropdownItem'
import DropdownDivider from '@/components/ui/Dropdown/components/DropdownDivider/DropdownDivider'
import { showToast } from '@/components/app/Toast/showToast'
import css from './TaskModal.module.css'
import TaskModalTitle from "./components/TaskModalTitle/TaskModalTitle";
import type { TaskModalTitleHandle } from "./components/TaskModalTitle/TaskModalTitle";
import TaskModalProperties from "./components/TaskModalProperties/TaskModalProperties";
import TaskModalDescription from "./components/TaskModalDescription/TaskModalDescription";
import type { TaskModalDescriptionHandle } from "./components/TaskModalDescription/TaskModalDescription";
import TaskModalComments from "./components/TaskModalComments/TaskModalComments";
import { createTaskSaga, moveTaskToListSaga, updateTaskDescriptionSaga } from "../../../store/tasks/tasksSlice.ts"
import { selectAllLists } from '../../../store/lists/listsSelectors'
import type { List } from '../../../contexts/ListsContext';

interface TaskModalProps {
    isOpen: boolean
    onClose: () => void
}

const TaskModal = ({ isOpen, onClose }: TaskModalProps) => {
    const modalRef = useRef<HTMLDivElement>(null)
    const titleRef = useRef<TaskModalTitleHandle>(null)
    const dispatch = useAppDispatch()
    const openDropdownId = useAppSelector(selectOpenDropdownId)
    const isConfirmDialogOpen = useAppSelector(state => state.ui.confirmDialog.isOpen)
    const openModal = useAppSelector(selectOpenModal)

    const { task, loading, updateTask, deleteTask } = useTask()
    const { workspace } = useWorkspace()
    const authUser = useAppSelector(selectAuth)

    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const [dropdownPage, setDropdownPage] = useState<'main' | 'move'>('main')
    const [numberCopied, setNumberCopied] = useState(false)
    const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const allLists = useAppSelector(selectAllLists)
    const projectLists = task
        ? allLists.filter((l: List) => l.projectId === task.projectId).sort((a: List, b: List) => a.order - b.order)
        : []

    const DROPDOWN_ID = `task-modal-options-${task?.id}`

    useEffect(() => {
        const shouldBeOpen = openDropdownId === DROPDOWN_ID
        if (isDropdownOpen !== shouldBeOpen) {
            setIsDropdownOpen(shouldBeOpen)
        }
    }, [openDropdownId, isDropdownOpen, DROPDOWN_ID])

    // The unwatched-notifications store (state.notifications.byId) only ever holds
    // watched === false docs, updated live by the workspace listener. While the modal
    // is open we keep marking any notification for this task as read — not just the
    // ones present at open time — so replies/mentions that arrive while the user is
    // looking at the task are dismissed before the delayed email sweep can pick them up.
    const allNotifications = useAppSelector(selectAllNotifications)
    const unwatchedKey = useMemo(() => {
        if (!task?.id) return ''
        return allNotifications
            .filter((n: Notification) => n.taskId === task.id)
            .map((n: Notification) => n.id)
            .join(',')
    }, [allNotifications, task?.id])

    useEffect(() => {
        if (isOpen && task?.id && unwatchedKey) {
            dispatch(markWatchedSaga(task.id))
        }
    }, [isOpen, task?.id, unwatchedKey, dispatch])

    useEffect(() => {
        if (!isOpen || !task?.id) return
        const taskId = task.id
        dispatch(subscribeTaskActivitySaga(taskId))
        return () => { dispatch(unsubscribeTaskActivitySaga(taskId)) }
    }, [isOpen, task?.id, dispatch])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (isConfirmDialogOpen) return
            if (openModal) return

            const isInDropdown = (event.composedPath() as Element[]).some(
                el => el?.getAttribute?.('data-dropdown-portal') === 'true'
            )
            if (isInDropdown) return

            const isInLightbox = (event.composedPath() as Element[]).some(
                el => el?.getAttribute?.('data-figma-lightbox') === 'true'
            )
            if (isInLightbox) return

            if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
                onClose()
            }
        }

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose()
            }
        }

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside)
            document.addEventListener('keydown', handleEscape)
            document.body.style.overflow = 'hidden'
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('keydown', handleEscape)
            document.body.style.removeProperty('overflow')
        }
    }, [isOpen, onClose, isConfirmDialogOpen, openModal])

    const handleDropdownClose = () => {
        dispatch(setOpenDropdown(null))
        setDropdownPage('main')
    }

    const handleMoveToList = (listId: string) => {
        if (!task) return
        dispatch(moveTaskToListSaga({ taskId: task.id, targetListId: listId, newOrder: 0 }))
        handleDropdownClose()
    }

    const getTaskUrl = (): string | null => {
        if (!task || !workspace) return null
        // A task open in the modal is always persisted (resolved by id), so it's never pending.
        return taskUrl(window.location.origin, {
            workspaceUrlId: workspace.workspaceUrlId,
            prefix: workspace.workspaceTaskPrefixId,
            number: task.number,
        })
    }

    // Copy from the number badge — shows an inline check instead of a toast.
    const handleCopyLink = () => {
        const url = getTaskUrl()
        if (!url) return
        navigator.clipboard.writeText(url)
        setNumberCopied(true)
        if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current)
        copyTimeoutRef.current = setTimeout(() => setNumberCopied(false), 1500)
    }

    // Copy from the options menu — the dropdown closes, so confirm with a toast.
    const handleCopyLinkFromMenu = () => {
        const url = getTaskUrl()
        if (!url) return
        navigator.clipboard.writeText(url)
        showToast('Link copied')
        handleDropdownClose()
    }

    useEffect(() => () => { if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current) }, [])

    const handleEdit = () => {
        handleDropdownClose()
        setTimeout(() => titleRef.current?.startEditing(), 0)
    }

    const handleOpenActivity = () => {
        if (!task) return
        handleDropdownClose()
        dispatch(setOpenModal({ type: 'taskActivity', taskId: task.id }))
    }

    const handleDuplicate = () => {
        if (!task || !authUser) return
        dispatch(createTaskSaga({
            listId: task.listId,
            name: `${task.name} (copy)`,
            projectId: task.projectId,
            workspaceId: task.workspaceId,
            userId: authUser.id,
            description: task.description,
        }))
        handleDropdownClose()
    }

    const handleDelete = () => {
        if (!task) return

        const taskTitle = task.name || 'this task'

        dispatch(openConfirmDialog({
            title: 'Delete task',
            text: `Are you sure you want to delete "${taskTitle}"? This cannot be undone.`,
            confirmButtonTitle: 'Delete',
            confirmButtonAction: async () => {
                await deleteTask()
                onClose()
            }
        }))

        handleDropdownClose()
    }

    const handleSaveTitle = async (name: string) => {
        await updateTask({ name })
    }

    const handlePropertiesUpdate = async (updates: { assigneeId?: string | null; statusId?: string; labels?: string[] }) => {
        await updateTask(updates)
    }

    const handleDescriptionSave = async (description: unknown) => {
        // Dispatch the dedicated description action (takeEvery), NOT updateTask (takeLatest):
        // routing the body through updateTask let a following field change (e.g. assignee) cancel
        // the in-flight description write and lose it (SCD-905).
        if (!task) return
        dispatch(updateTaskDescriptionSaga({ taskId: task.id, description }))
    }

    const handleImageUpload = useCallback(async (file: File) => {
        if (!task || !authUser) throw new Error('Not ready')
        return uploadAttachment({ file, workspaceId: task.workspaceId, userId: authUser.id, taskId: task.id, projectId: task.projectId })
    }, [task, authUser])

    // The whole modal (backdrop included) is a drop target: dropped files land in the
    // description via the editor's upload flow. A drag carrying only a URL (an image dragged
    // from another app/tab, e.g. Slack) has no File — the browser's default is to navigate to
    // it, losing the app — so it's always intercepted and fetched + attached when possible.
    const descriptionRef = useRef<TaskModalDescriptionHandle>(null)
    const dragDepthRef = useRef(0)
    const [isDraggingOver, setIsDraggingOver] = useState(false)

    const isMediaDrag = (e: React.DragEvent) => {
        const types = e.dataTransfer.types
        // An in-editor drag (moving a node/selection inside Plate) must keep its default handling.
        if (types.includes('application/x-slate-fragment')) return false
        return types.includes('Files') || types.includes('text/uri-list')
    }

    const handleDragEnter = (e: React.DragEvent) => {
        if (!isMediaDrag(e)) return
        e.preventDefault()
        dragDepthRef.current += 1
        setIsDraggingOver(true)
    }

    const handleDragOver = (e: React.DragEvent) => {
        if (!isMediaDrag(e)) return
        e.preventDefault()
        e.dataTransfer.dropEffect = 'copy'
    }

    const handleDragLeave = (e: React.DragEvent) => {
        if (!isMediaDrag(e)) return
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1)
        if (dragDepthRef.current === 0) setIsDraggingOver(false)
    }

    // Capture phase, so the hint is dismissed even when a nested editor consumes the drop
    // (its handler stops propagation, which would skip a bubble-phase reset).
    const handleDropCapture = () => {
        dragDepthRef.current = 0
        setIsDraggingOver(false)
    }

    // A cancelled drag (Esc, or dropping outside the window) doesn't reliably fire a final
    // dragleave, which would leave the hint stuck. Mouse movement means no drag is active.
    const handleMouseMove = isDraggingOver ? handleDropCapture : undefined

    const attachFromUrl = async (uri: string) => {
        try {
            const res = await fetch(uri)
            if (!res.ok) throw new Error(`HTTP ${res.status}`)
            const blob = await res.blob()
            if (!blob.type.startsWith('image/')) throw new Error(`unsupported content type: ${blob.type}`)
            const ext = (blob.type.split('/')[1] || 'png').split('+')[0]
            let name = ''
            try { name = decodeURIComponent(new URL(uri).pathname.split('/').pop() || '') } catch { /* fall back below */ }
            if (!name || !name.includes('.')) name = `image-${Date.now()}.${ext}`
            const accepted = descriptionRef.current?.insertFiles([new File([blob], name, { type: blob.type })])
            if (!accepted) throw new Error('description not ready')
        } catch (err) {
            // Typical for sources that require auth or don't allow cross-origin reads (e.g. a Slack
            // image drag carries a private files.slack.com URL only Slack itself can serve). Point
            // at copy/paste — the clipboard carries real pixels, so it works where the drag can't.
            console.error('Attach from dragged URL failed:', err)
            showToast("Couldn't attach — copy the image and paste it instead.", { type: 'error' })
        }
    }

    const handleModalDrop = (e: React.DragEvent) => {
        if (!isMediaDrag(e)) return
        const files = Array.from(e.dataTransfer.files)
        if (files.length) {
            // The description editor consumes drops on itself (capture + stopPropagation), so a
            // file drop reaching here landed elsewhere on the modal — append it to the description.
            e.preventDefault()
            const accepted = descriptionRef.current?.insertFiles(files)
            if (!accepted) showToast('Still loading — try dropping again.')
            return
        }
        // URL-only drag. If a nested handler already acted on it (defaultPrevented), leave it be;
        // otherwise stop the browser from navigating away and try to fetch + attach the media.
        if (e.defaultPrevented) return
        e.preventDefault()
        const uriList = e.dataTransfer.getData('text/uri-list')
        const uri = uriList.split('\n').map(l => l.trim()).find(l => l && !l.startsWith('#'))
        if (!uri) return
        void attachFromUrl(uri)
    }

    if (!isOpen || loading || !task) return null

    return (
        <div
            className={css.overlay}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleModalDrop}
            onDropCapture={handleDropCapture}
            onMouseMove={handleMouseMove}
        >
            {isDraggingOver && (
                <div className={css.drop_overlay}>
                    <div className={css.drop_overlay_label}>Drop to attach</div>
                </div>
            )}
            <div ref={modalRef} className={css.modal} data-testid="task-modal">
                <div className={css.buttons}>
                    <DropdownWrapper>
                        <IconButton icon={<IconOptions />} onClick={() => { dispatch(setOpenDropdown(isDropdownOpen ? null : DROPDOWN_ID)); setDropdownPage('main') }} />
                        <Dropdown isOpen={isDropdownOpen} align={"right"} onClose={handleDropdownClose}>
                            {dropdownPage === 'main' ? (
                                <>
                                    <DropdownItem
                                        title="Edit"
                                        onClick={handleEdit}
                                    />
                                    <DropdownItem
                                        title="Duplicate"
                                        onClick={handleDuplicate}
                                    />
                                    {projectLists.length > 0 && (
                                        <DropdownItem
                                            title="Move to"
                                            rightIcon={<IconArrowRight />}
                                            onClick={() => setDropdownPage('move')}
                                        />
                                    )}
                                    <DropdownItem
                                        title="Activity"
                                        onClick={handleOpenActivity}
                                    />
                                    <DropdownItem
                                        title="Copy link"
                                        onClick={handleCopyLinkFromMenu}
                                    />
                                    <DropdownDivider />
                                    <DropdownItem
                                        title="Delete"
                                        onClick={handleDelete}
                                        danger={true}
                                    />
                                </>
                            ) : (
                                <>
                                    <DropdownItem
                                        icon={<IconArrowLeft />}
                                        title="Move to"
                                        muted={true}
                                        onClick={() => setDropdownPage('main')}
                                    />
                                    <DropdownDivider />
                                    {projectLists.map((list: List) => {
                                        const isCurrent = list.id === task?.listId
                                        return (
                                            <DropdownItem
                                                key={list.id}
                                                title={list.name || 'Untitled'}
                                                rightIcon={isCurrent ? <IconCheck /> : undefined}
                                                onClick={isCurrent ? undefined : () => handleMoveToList(list.id)}
                                            />
                                        )
                                    })}
                                </>
                            )}
                        </Dropdown>
                    </DropdownWrapper>
                    <IconButton icon={<IconClose />} onClick={onClose} />
                </div>
                {workspace && (
                    <div className={css.number}>
                        <ButtonRound ghost onClick={handleCopyLink}>
                            {numberCopied ? (
                                <span className={css.number_check}><IconCheck /></span>
                            ) : (
                                <span className={css.number_text}>{workspace.workspaceTaskPrefixId}-{task.number}</span>
                            )}
                        </ButtonRound>
                    </div>
                )}

                <div className={css.header}>
                    <TaskModalTitle ref={titleRef} name={task.name} onSave={handleSaveTitle} />
                </div>

                <TaskModalProperties
                    taskId={task.id}
                    projectId={task.projectId}
                    assigneeId={task.assigneeId}
                    statusId={task.statusId}
                    labelIds={task.labels}
                    onUpdate={handlePropertiesUpdate}
                />

                <TaskModalDescription
                    ref={descriptionRef}
                    description={task.description}
                    onSave={handleDescriptionSave}
                    onFileUpload={authUser ? handleImageUpload : undefined}
                />

                <TaskModalComments />
            </div>
        </div>
    )
}

export default TaskModal
